/**
 * lottie-web 読み込みユーティリティ
 *
 * 同一ページに複数の Lottie（同一 JSON 含む）が存在しても SVG ID が衝突しないよう、
 * loadAnimation の直前にユニークな ID プレフィックスを付与する。
 */
import lottie, {
	type AnimationConfigWithData,
	type AnimationConfigWithPath,
	type AnimationItem,
} from 'lottie-web';

let lottieInstanceSeq = 0;

type LoadLottieParams = (
	| AnimationConfigWithPath
	| AnimationConfigWithData
) & {
	/** デバッグ用のスコープ名（ブロック名など）。固定数に依存しない。 */
	idPrefixScope?: string;
};

/**
 * インスタンスごとにユニークな SVG ID プレフィックスを生成する。
 */
function createLottieIdPrefix(
	scope?: string,
	container?: Element | null
): string {
	lottieInstanceSeq += 1;
	const seq = lottieInstanceSeq;

	const scopePart = scope
		? `${scope.replace(/[^a-zA-Z0-9_-]/g, '-')}-`
		: '';

	const containerPart =
		container instanceof HTMLElement && container.id
			? `${container.id}-`
			: '';

	const unique =
		typeof crypto !== 'undefined' &&
		typeof crypto.randomUUID === 'function'
			? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
			: `${ Date.now().toString( 36 ) }${ Math.random()
					.toString( 36 )
					.slice( 2, 8 ) }`;

	return `lottie-${ scopePart }${ containerPart }${ seq }-${ unique }-`;
}

/**
 * setIDPrefix を適用してから Lottie アニメーションを読み込む。
 */
export function loadLottieAnimation(
	params: LoadLottieParams
): AnimationItem {
	const { idPrefixScope, ...animationParams } = params;
	const prefix = createLottieIdPrefix(
		idPrefixScope,
		animationParams.container as Element
	);

	lottie.setIDPrefix( prefix );

	return lottie.loadAnimation( animationParams );
}
